import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HomeIcon from "@mui/icons-material/Home";
import PersonIcon from "@mui/icons-material/Person";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  CircularProgress,
  Popover,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { COLORS } from "app/theme";
import { guid } from "common/guid";
import { useTranslation } from "common/locales";
import { queryKeys } from "common/queryKeys";
import type {
  AnimalGroup,
  Field,
  ManualDMInputResponse,
  MoveEvent,
} from "model";
import {
  EventType,
  getAllFieldsForAnimalGroupIdFromArray,
  useAnimalGroupMoveEvents,
  useCreateReading,
  useDeleteReading,
  useFields,
  useUpdateReading,
} from "model";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useUIStore } from "stores";
import type { RotationAnalysis } from "./rotationPlanner";
import { analyzeRotation } from "./rotationPlanner";

// ─── Types ───────────────────────────────────────────────────────────────────

type EventStatus = "accepted" | "proposed";
type EventOrigin = "user" | "machine";

interface GridCell {
  fieldGuid: string;
  fieldName: string;
  rotation: number;
  moveEvent?: MoveEvent;
  status: EventStatus | null; // null = empty cell
  origin: EventOrigin; // user input or machine (satellite) detected
  dateIn?: string;
  dateOut?: string | null;
  flagged?: boolean; // unrealistically long event
  overlapWarning?: string; // overlaps with another event in time
  isShed?: boolean; // movedToShed event — has no field
}

// ─── Constants ───────────────────────────────────────────────────────────────

const MAX_SENSIBLE_GRAZING_DAYS = 21;
const SECTION_CARD_SX = {
  backgroundColor: "white",
  borderRadius: "16px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  p: { xs: 1.5, sm: 2 },
  mb: 2,
} as const;

const STATUS_COLORS: Record<
  EventStatus,
  { bg: string; bgLight: string; border: string; text: string }
> = {
  accepted: {
    bg: COLORS.dahlia[300],
    bgLight: COLORS.dahlia[100],
    border: COLORS.dahlia[500],
    text: COLORS.dahlia[900],
  },
  proposed: {
    bg: COLORS.bellflower[300],
    bgLight: COLORS.bellflower[100],
    border: COLORS.bellflower[500],
    text: COLORS.bellflower[900],
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function visibleRotationMoveEvents(
  moveEvents: MoveEvent[]
): MoveEvent[] {
  return moveEvents.filter((ev) => ev.verificationStatus !== "Rejected");
}

export function isGrazingEventSaveDisabled(dateOrderInvalid: boolean): boolean {
  return dateOrderInvalid;
}

function getSeasonYears(moveEvents: MoveEvent[]): number[] {
  const years = new Set<number>();

  for (const ev of moveEvents) {
    if (ev.dateTimeIn) years.add(new Date(ev.dateTimeIn).getFullYear());
    if (ev.dateTimeOut) years.add(new Date(ev.dateTimeOut).getFullYear());
  }

  if (years.size === 0) years.add(new Date().getFullYear());
  return Array.from(years).sort((a, b) => b - a);
}

function getDaysBetween(start: string, end: string | null): number {
  const endDate = end ? new Date(end) : new Date();
  const startDate = new Date(start);
  return Math.max(
    1,
    Math.round((endDate.getTime() - startDate.getTime()) / 86400000)
  );
}

interface SeasonInfo {
  seasonStart: Date | null;
  seasonEnd: Date | null;
  isCurrentSeason: boolean;
  warnings: string[];
}

/**
 * Get season date range from move events (first event start → last event end).
 * Detects nonsensical events: cross-year spans, events outside the season year.
 */
function getSeasonDateRange(
  moveEvents: MoveEvent[],
  seasonYear: number,
  t: ReturnType<typeof useTranslation>["t"]
): SeasonInfo {
  const isCurrentSeason = seasonYear === new Date().getFullYear();
  const warnings: string[] = [];

  let earliest: Date | null = null;
  let latest: Date | null = null;

  const filteredMoves = moveEvents.filter(
    (ev): ev is MoveEvent & { dateTimeIn: string } =>
      ev.dateTimeIn != null &&
      new Date(ev.dateTimeIn).getFullYear() === seasonYear
  );
  for (const ev of filteredMoves) {
    const inDate = new Date(ev.dateTimeIn);
    if (!earliest || inDate < earliest) earliest = inDate;

    if (ev.dateTimeOut) {
      const outDate = new Date(ev.dateTimeOut);
      if (!latest || outDate > latest) latest = outDate;
      if (outDate.getFullYear() !== seasonYear) {
        warnings.push(
          t("rotationGrid.warningCrossYear", { field: ev.fieldName ?? "" })
        );
      }
    }
  }

  if (!earliest)
    return { seasonStart: null, seasonEnd: null, isCurrentSeason, warnings };

  if (isCurrentSeason) {
    latest = null;
  } else {
    if (latest && latest.getFullYear() > seasonYear) {
      latest = new Date(seasonYear, 11, 31);
    }
    if (!latest) {
      let latestIn: Date | null = null;
      for (const ev of filteredMoves) {
        const d = new Date(ev.dateTimeIn);
        if (!latestIn || d > latestIn) latestIn = d;
      }
      latest = latestIn;
    }
  }

  return {
    seasonStart: earliest,
    seasonEnd: latest,
    isCurrentSeason,
    warnings,
  };
}

/**
 * Group move events into rotation cycles.
 * A new rotation starts when:
 * - We encounter a field already visited in the current rotation AND
 * - We've visited at least 1/3 of the total unique fields (avoids splitting
 *   on same-day or back-to-back moves to the same field)
 *
 * Open events (no dateTimeOut — i.e. the animal group is currently grazing
 * there) are pinned to the last rotation regardless of when they started, so
 * "where the animals are now" is always the rightmost column.
 *
 * After grouping, empty rotations are removed and gaps are compacted.
 */
export function groupIntoRotations(
  moveEvents: MoveEvent[],
  seasonYear: number
): Map<number, MoveEvent[]> {
  const isCurrentSeason = seasonYear === new Date().getFullYear();
  const filtered = moveEvents.filter((ev) => {
    // Open events (still ongoing) belong only in the current-year view, so
    // the animal group's current location shows up there once and nowhere
    // else — even if the event started in a previous year.
    if (ev.dateTimeOut === null) return isCurrentSeason;
    const seasonAnchor = ev.dateTimeIn ?? ev.dateTimeOut;
    if (!seasonAnchor) return false;
    return new Date(seasonAnchor).getFullYear() === seasonYear;
  });

  const closedEvents = filtered.filter((ev) => ev.dateTimeOut !== null);
  const allOpenEvents = filtered.filter((ev) => ev.dateTimeOut === null);
  // If any open event is assigned to this group, hide unassigned open
  // candidates — the group's actual location wins over an orphan match.
  const hasAssignedOpen = allOpenEvents.some(
    (ev) => ev.animalGroupGuid != null
  );
  const openEvents = hasAssignedOpen
    ? allOpenEvents.filter((ev) => ev.animalGroupGuid != null)
    : allOpenEvents;

  // Count unique fields to set a sensible threshold
  const uniqueFields = new Set(
    closedEvents.map((ev) => ev.fieldGuid).filter(Boolean)
  );
  const minBeforeSplit = Math.max(2, Math.ceil(uniqueFields.size / 3));

  const rotations = new Map<number, MoveEvent[]>();
  let currentRotation = 1;
  const visitedInCurrentRotation = new Set<string>();

  for (const ev of closedEvents) {
    const key = ev.fieldGuid ?? "__shed__";
    if (
      visitedInCurrentRotation.has(key) &&
      visitedInCurrentRotation.size >= minBeforeSplit
    ) {
      currentRotation++;
      visitedInCurrentRotation.clear();
    }
    visitedInCurrentRotation.add(key);
    if (!rotations.has(currentRotation)) {
      rotations.set(currentRotation, []);
    }
    const bucket = rotations.get(currentRotation);
    if (bucket) bucket.push(ev);
  }

  // Compact: remove empty rotations and re-number
  const compacted = new Map<number, MoveEvent[]>();
  let idx = 1;
  for (const [, events] of rotations) {
    if (events.length > 0) {
      compacted.set(idx++, events);
    }
  }

  // Pin all open events to the last rotation (creating one if there are no
  // closed events yet).
  if (openEvents.length > 0) {
    const lastKey = compacted.size === 0 ? 1 : compacted.size;
    const lastBucket = compacted.get(lastKey) ?? [];
    lastBucket.push(...openEvents);
    compacted.set(lastKey, lastBucket);
  }

  return compacted;
}

/**
 * Sort fields by:
 * 1. Chronological order of first grazing
 * 2. For ungrazed fields: highest dryMatterNow first (likely grazed next)
 * 3. Tie-break by geographic latitude (north → south)
 */
function sortFieldsByRotationOrder(
  fields: Field[],
  moveEvents: MoveEvent[],
  seasonYear: number
): Field[] {
  const firstGrazedDate = new Map<string, number>();

  for (const ev of moveEvents) {
    if (!ev.fieldGuid || !ev.dateTimeIn) continue;
    if (new Date(ev.dateTimeIn).getFullYear() !== seasonYear) continue;
    const time = new Date(ev.dateTimeIn).getTime();
    const existing = firstGrazedDate.get(ev.fieldGuid);
    if (existing == null || time < existing) {
      firstGrazedDate.set(ev.fieldGuid, time);
    }
  }

  return [...fields].sort((a, b) => {
    const aTime = firstGrazedDate.get(a.guid);
    const bTime = firstGrazedDate.get(b.guid);

    // Grazed fields first, in chronological order of first grazing
    if (aTime != null && bTime != null) return aTime - bTime;
    if (aTime != null) return -1;
    if (bTime != null) return 1;

    // Ungrazed: highest dryMatterNow first (likely next to be grazed)
    const dmDiff = (b.dryMatterNow ?? 0) - (a.dryMatterNow ?? 0);
    if (Math.abs(dmDiff) > 0.0001) return dmDiff;

    // Tie-break: latitude (north to south)
    const aLat = getFieldLatitude(a);
    const bLat = getFieldLatitude(b);
    return bLat - aLat;
  });
}

function getFieldLatitude(field: Field): number {
  try {
    const geom = field.geom;
    if (!geom) return 0;
    const coords =
      geom.geometry?.type === "Polygon"
        ? (geom.geometry as { coordinates: number[][][] }).coordinates[0]
        : geom.geometry?.type === "MultiPolygon"
        ? (geom.geometry as { coordinates: number[][][][] }).coordinates[0][0]
        : null;
    if (!coords || coords.length === 0) return 0;
    return coords.reduce((sum, c) => sum + c[1], 0) / coords.length;
  } catch {
    return 0;
  }
}

function getCellStatus(moveEvent?: MoveEvent): EventStatus | null {
  const vs = moveEvent?.verificationStatus;
  if (!vs) return null;
  return vs === "Proposed" ? "proposed" : "accepted";
}

function getCellOrigin(moveEvent?: MoveEvent): EventOrigin {
  return moveEvent?.kind === "machine" ? "machine" : "user";
}

export function parseDryMatter(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/** Pre-cover requires move-in date; post-cover requires move-out date. */
export function parseBiomassForGrazingDates(
  dates: { dateIn: string; dateOut: string },
  inputs: { startDryMatter: string; endDryMatter: string }
): { startDryMatter: number | null; endDryMatter: number | null } {
  return {
    startDryMatter: dates.dateIn ? parseDryMatter(inputs.startDryMatter) : null,
    endDryMatter: dates.dateOut ? parseDryMatter(inputs.endDryMatter) : null,
  };
}

export type GrazingEditDateErrors = { dateIn: boolean; dateOut: boolean };

/** Which date fields must be filled before save (biomass or required move-in). */
export function getGrazingEditDateErrors(
  dates: { dateIn: string; dateOut: string },
  inputs: { startDryMatter: string; endDryMatter: string },
  options: { requireDateIn?: boolean } = {}
): GrazingEditDateErrors {
  const requireDateIn = options.requireDateIn ?? true;
  const hasPre = parseDryMatter(inputs.startDryMatter) != null;
  const hasPost = parseDryMatter(inputs.endDryMatter) != null;
  return {
    dateIn: (requireDateIn && !dates.dateIn) || (hasPre && !dates.dateIn),
    dateOut: hasPost && !dates.dateOut,
  };
}

export function hasGrazingEditDateErrors(
  errors: GrazingEditDateErrors
): boolean {
  return errors.dateIn || errors.dateOut;
}

export function getCoverWarning(moveEvent?: MoveEvent): string | null {
  if (!moveEvent) return null;
  const pre = moveEvent.startDryMatter;
  const post = moveEvent.endDryMatter;
  if (pre != null && post != null && post > pre) {
    return `Post-grazing cover (${Math.round(
      post
    )}) is higher than pre-grazing cover (${Math.round(
      pre
    )}) — this should not happen.`;
  }
  return null;
}

/** Move-out recorded without a move-in date — incomplete rotation data. */
export function isMissingMoveInEvent(moveEvent?: MoveEvent): boolean {
  return (
    !!moveEvent && moveEvent.dateTimeIn == null && moveEvent.dateTimeOut != null
  );
}

export type GrazingEditDateTimeFields = {
  dateIn: string;
  timeIn: string;
  dateOut: string;
  timeOut: string;
};

/** Local date (+ optional time) as epoch ms for rotation editor ordering. */
export function grazingEditDateTimeMs(
  date: string,
  time: string,
  useTime: boolean
): number | null {
  if (!date) return null;
  const t = useTime && time ? time : "12:00";
  const ms = new Date(`${date}T${t}:00`).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function isGrazingEditDateOrderInvalid(
  dateIn: string,
  timeIn: string,
  dateOut: string,
  timeOut: string,
  useTime: boolean
): boolean {
  if (!dateIn || !dateOut) return false;
  if (dateOut < dateIn) return true;
  if (dateOut > dateIn) return false;
  // Same calendar day
  if (!useTime) return false;
  // With times visible, wait until both are set before flagging an error
  if (!timeIn || !timeOut) return false;
  return timeOut < timeIn;
}

/** After start date changes, clamp start time when it lands on the end date. */
export function clampGrazingEditAfterDateInChange(
  fields: GrazingEditDateTimeFields,
  nextDateIn: string,
  useTime: boolean
): GrazingEditDateTimeFields {
  const next = { ...fields, dateIn: nextDateIn };
  if (
    useTime &&
    nextDateIn &&
    fields.dateOut &&
    nextDateIn === fields.dateOut &&
    fields.timeOut
  ) {
    if (!fields.timeIn || fields.timeIn > fields.timeOut) {
      next.timeIn = fields.timeOut;
    }
  }
  return next;
}

/** After end date changes, clamp end time when it lands on the start date. */
export function clampGrazingEditAfterDateOutChange(
  fields: GrazingEditDateTimeFields,
  nextDateOut: string,
  useTime: boolean
): GrazingEditDateTimeFields {
  const next = { ...fields, dateOut: nextDateOut };
  if (
    useTime &&
    fields.dateIn &&
    nextDateOut &&
    nextDateOut === fields.dateIn &&
    fields.timeIn
  ) {
    if (!fields.timeOut || fields.timeOut < fields.timeIn) {
      next.timeOut = fields.timeIn;
    }
  }
  return next;
}

/** After start time changes, clamp so start <= end on the same day. */
export function clampGrazingEditAfterTimeInChange(
  fields: GrazingEditDateTimeFields,
  nextTimeIn: string,
  useTime: boolean
): GrazingEditDateTimeFields {
  const next = { ...fields, timeIn: nextTimeIn };
  if (
    useTime &&
    fields.dateIn &&
    fields.dateOut &&
    fields.dateIn === fields.dateOut &&
    nextTimeIn &&
    fields.timeOut &&
    nextTimeIn > fields.timeOut
  ) {
    next.timeIn = fields.timeOut;
  }
  return next;
}

/** After end time changes, clamp so end >= start on the same day. */
export function clampGrazingEditAfterTimeOutChange(
  fields: GrazingEditDateTimeFields,
  nextTimeOut: string,
  useTime: boolean
): GrazingEditDateTimeFields {
  const next = { ...fields, timeOut: nextTimeOut };
  if (
    useTime &&
    fields.dateIn &&
    fields.dateOut &&
    fields.dateIn === fields.dateOut &&
    fields.timeIn &&
    nextTimeOut &&
    nextTimeOut < fields.timeIn
  ) {
    next.timeOut = fields.timeIn;
  }
  return next;
}

function buildGrazingEditIso(
  day: string,
  time: string,
  showTime: boolean,
  originalIso: string | null,
  sameDayCounterpartIso: string | null
): string {
  if (!showTime && originalIso && originalIso.slice(0, 10) === day) {
    return originalIso;
  }
  if (showTime && time) {
    return `${day}T${time}:00.000Z`;
  }
  if (sameDayCounterpartIso && sameDayCounterpartIso.slice(0, 10) === day) {
    return `${day}T${sameDayCounterpartIso.slice(11)}`;
  }
  return `${day}T12:00:00.000Z`;
}

/** Build start/end ISO strings for saving a rotation editor cell edit. */
export function buildGrazingEditIsoPair(
  fields: GrazingEditDateTimeFields & { showTime: boolean },
  originals: {
    dateTimeIn?: string | null;
    dateTimeOut?: string | null;
  }
): { dateIn: string; dateOut: string | null } {
  const { dateIn, timeIn, dateOut, timeOut, showTime } = fields;
  const sameDay = Boolean(dateIn && dateOut && dateIn === dateOut);

  const dateInIso = dateIn
    ? buildGrazingEditIso(
        dateIn,
        timeIn,
        showTime,
        originals.dateTimeIn ?? null,
        null
      )
    : "";

  const dateOutIso = dateOut
    ? buildGrazingEditIso(
        dateOut,
        timeOut,
        showTime,
        originals.dateTimeOut ?? null,
        sameDay ? dateInIso : null
      )
    : null;

  // Close-only: user added a start date on the same day as the preserved end.
  if (
    !showTime &&
    sameDay &&
    !originals.dateTimeIn &&
    originals.dateTimeOut &&
    dateOutIso
  ) {
    return {
      dateIn: `${dateIn}T${dateOutIso.slice(11)}`,
      dateOut: dateOutIso,
    };
  }

  return { dateIn: dateInIso, dateOut: dateOutIso };
}

const DAY_MS = 86400000;

const RANGE_DATE_FMT: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
};

function formatEventRange(
  dateTimeIn: string,
  dateTimeOut: string | null
): string {
  const start = new Date(dateTimeIn).toLocaleDateString(
    undefined,
    RANGE_DATE_FMT
  );
  const end = dateTimeOut
    ? new Date(dateTimeOut).toLocaleDateString(undefined, RANGE_DATE_FMT)
    : "ongoing";
  return `${start} → ${end}`;
}

/**
 * Compares events on day-level boundaries (intervals are half-open
 * [start, end) so a same-day handover is OK). Open events extend to
 * Infinity. All moveEvents in the rotation editor belong to the same animal
 * group, so any pair that overlaps in time is a conflict — either two events
 * on the same field or the group being in two places at once.
 *
 * The warning text names the conflicting event AND its date range so the
 * farmer can locate the partner without scrolling through years.
 */
export function buildOverlapWarningMap(
  moveEvents: MoveEvent[]
): Map<string, string> {
  // Orphan (unattached) events are candidates only — they may have actually
  // belonged to a different animal group historically, so we don't let them
  // generate conflict warnings until the farmer explicitly attaches them.
  const items = moveEvents
    .filter(
      (ev): ev is MoveEvent & { dateTimeIn: string } =>
        ev.animalGroupGuid != null && ev.dateTimeIn != null
    )
    .map((ev) => ({
      guid: ev.guid,
      fieldGuid: ev.fieldGuid,
      fieldName: ev.fieldName,
      range: formatEventRange(ev.dateTimeIn, ev.dateTimeOut),
      startDay: Math.floor(new Date(ev.dateTimeIn).getTime() / DAY_MS),
      endDay: ev.dateTimeOut
        ? Math.floor(new Date(ev.dateTimeOut).getTime() / DAY_MS)
        : Number.POSITIVE_INFINITY,
    }));

  const messages = new Map<string, string>();
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const a = items[i];
      const b = items[j];
      const overlap = a.startDay < b.endDay && b.startDay < a.endDay;
      if (!overlap) continue;
      const sameField = a.fieldGuid != null && a.fieldGuid === b.fieldGuid;
      const labelFor = (other: typeof a) =>
        `${other.fieldName ?? "another field"} (${other.range})`;
      const aMsg = sameField
        ? `Overlaps in time with another event on the same field (${b.range}).`
        : `Overlaps in time with the event on ${labelFor(
            b
          )} — the animal group can't be in two places at once.`;
      const bMsg = sameField
        ? `Overlaps in time with another event on the same field (${a.range}).`
        : `Overlaps in time with the event on ${labelFor(
            a
          )} — the animal group can't be in two places at once.`;
      if (!messages.has(a.guid)) messages.set(a.guid, aMsg);
      if (!messages.has(b.guid)) messages.set(b.guid, bMsg);
    }
  }
  return messages;
}

export function buildReadingPayload(
  moveEvent: MoveEvent,
  fieldGuid: string,
  animalGroupGuid: string | null,
  overrides: {
    dateTimeIn?: string;
    dateTimeOut?: string | null;
    startDryMatter?: number | null;
    endDryMatter?: number | null;
    verificationStatus?: "Proposed" | "Accepted" | "Rejected";
  } = {}
): ManualDMInputResponse {
  const dateTimeIn = overrides.dateTimeIn ?? moveEvent.dateTimeIn;
  const dateTimeOut =
    overrides.dateTimeOut === null
      ? undefined
      : overrides.dateTimeOut ?? moveEvent.dateTimeOut ?? undefined;
  const verificationStatus =
    overrides.verificationStatus ?? moveEvent.verificationStatus ?? "Accepted";
  const preEventCover =
    "startDryMatter" in overrides
      ? overrides.startDryMatter ?? undefined
      : moveEvent.startDryMatter ?? undefined;
  const postEventCover =
    "endDryMatter" in overrides
      ? overrides.endDryMatter ?? undefined
      : moveEvent.endDryMatter ?? undefined;
  const measurementStart = dateTimeIn ?? dateTimeOut;
  if (!measurementStart)
    throw new Error("A grazing event requires a start or end date");

  return {
    guid: moveEvent.guid,
    manualDryMatterInput: {
      field: fieldGuid,
      dateTimeMeasurementStart: measurementStart,
      dateTimeMeasurementEnd: dateTimeOut ?? measurementStart,
      kind: moveEvent.kind ?? "user",
      verificationStatus,
      event: {
        eventType: EventType.GrazingEvent,
        dateTimeIn: dateTimeIn ?? undefined,
        dateTimeOut,
        preEventCover,
        postEventCover,
        // Omit the field when caller passes null so the backend does not
        // attach an animal group as a side effect (e.g., during a
        // rejection of an unattached event).
        ...(animalGroupGuid != null ? { animalGroupGuid } : {}),
      },
    },
  };
}

// ─── Rotation Column Builder ─────────────────────────────────────────────────

interface MissingField {
  guid: string;
  name: string;
  isAssigned: boolean;
}

interface RotationColumn {
  rotation: number;
  totalDays: number;
  events: GridCell[];
}

function buildRotationColumns(
  moveEvents: MoveEvent[],
  seasonYear: number,
  overlapByGuid: Map<string, string>
): RotationColumn[] {
  const rotations = groupIntoRotations(moveEvents, seasonYear);

  const columns: RotationColumn[] = [];

  for (const [rotNum, events] of rotations) {
    const cells: GridCell[] = [];
    let totalDays = 0;

    for (const ev of events) {
      const isShed = ev.movedToShed === true;
      if (!ev.fieldGuid && !isShed) continue;

      const hasEnd = !!ev.dateTimeOut;
      const days =
        hasEnd && ev.dateTimeIn
          ? getDaysBetween(ev.dateTimeIn, ev.dateTimeOut)
          : null;
      if (days != null) totalDays += days;

      cells.push({
        fieldGuid: ev.fieldGuid ?? "",
        fieldName: isShed ? "Shed" : ev.fieldName ?? "—",
        rotation: rotNum,
        moveEvent: ev,
        status: getCellStatus(ev),
        origin: getCellOrigin(ev),
        dateIn: ev.dateTimeIn ?? undefined,
        dateOut: ev.dateTimeOut,
        overlapWarning: overlapByGuid.get(ev.guid),
        isShed,
      });
    }

    columns.push({
      rotation: rotNum,
      totalDays: Math.round(totalDays * 10) / 10,
      events: cells,
    });
  }

  return columns;
}

// ─── Legacy Grid Builder (kept for ReviewPanel compatibility) ─────────────────

function buildGrid(
  fields: Field[],
  moveEvents: MoveEvent[],
  seasonYear: number,
  overlapByGuid: Map<string, string>
): { grid: GridCell[][]; rotationCount: number } {
  const rotations = groupIntoRotations(moveEvents, seasonYear);
  const rotationCount = Math.max(rotations.size, 3);

  const moveByFieldAndRotation = new Map<string, Map<number, MoveEvent>>();
  for (const [rotNum, events] of rotations) {
    for (const ev of events) {
      if (!ev.fieldGuid) continue;
      if (!moveByFieldAndRotation.has(ev.fieldGuid)) {
        moveByFieldAndRotation.set(ev.fieldGuid, new Map());
      }
      const fieldMap = moveByFieldAndRotation.get(ev.fieldGuid);
      if (fieldMap) fieldMap.set(rotNum, ev);
    }
  }

  const grid: GridCell[][] = fields.map((field) => {
    const fieldMoves = moveByFieldAndRotation.get(field.guid);

    const row: GridCell[] = [];
    for (let rot = 1; rot <= rotationCount; rot++) {
      const moveEvent = fieldMoves?.get(rot);

      if (moveEvent) {
        const hasEnd = !!moveEvent.dateTimeOut;
        const days =
          hasEnd && moveEvent.dateTimeIn
            ? getDaysBetween(moveEvent.dateTimeIn, moveEvent.dateTimeOut)
            : null;

        row.push({
          fieldGuid: field.guid,
          fieldName: field.name,
          rotation: rot,
          moveEvent,
          status: getCellStatus(moveEvent),
          origin: getCellOrigin(moveEvent),
          dateIn: moveEvent.dateTimeIn ?? undefined,
          dateOut: moveEvent.dateTimeOut,
          flagged: hasEnd && days != null && days > MAX_SENSIBLE_GRAZING_DAYS,
          overlapWarning: overlapByGuid.get(moveEvent.guid),
        });
      } else {
        row.push({
          fieldGuid: field.guid,
          fieldName: field.name,
          rotation: rot,
          status: null,
          origin: "user",
        });
      }
    }
    return row;
  });

  return { grid, rotationCount };
}

/**
 * Suggest a date range for a new event in an empty cell,
 * based on surrounding events in the same rotation.
 */
const MIN_SUGGESTED_DAYS = 2;

function suggestEventDates(
  grid: GridCell[][],
  fieldIndex: number,
  rotationIndex: number
): { suggestedIn: Date | null; suggestedOut: Date | null } {
  // Look for the nearest filled cell above in the same rotation
  let prevOut: Date | null = null;
  for (let fi = fieldIndex - 1; fi >= 0; fi--) {
    const cell = grid[fi][rotationIndex];
    if (cell.status === null) continue;
    if (cell.dateOut) {
      prevOut = new Date(cell.dateOut);
    } else if (cell.dateIn) {
      // If no dateOut, estimate: dateIn + 3 days
      prevOut = new Date(new Date(cell.dateIn).getTime() + 3 * 86400000);
    }
    break;
  }

  // Look for the nearest filled cell below in the same rotation
  let nextIn: Date | null = null;
  for (let fi = fieldIndex + 1; fi < grid.length; fi++) {
    const cell = grid[fi][rotationIndex];
    if (cell.status === null) continue;
    if (cell.dateIn) {
      nextIn = new Date(cell.dateIn);
    }
    break;
  }

  let suggestedIn: Date | null = null;
  let suggestedOut: Date | null = null;

  if (prevOut && nextIn) {
    suggestedIn = prevOut;
    suggestedOut = nextIn;
  } else if (prevOut) {
    suggestedIn = prevOut;
    suggestedOut = new Date(prevOut.getTime() + 3 * 86400000);
  } else if (nextIn) {
    suggestedIn = new Date(nextIn.getTime() - 3 * 86400000);
    suggestedOut = nextIn;
  }

  // Ensure minimum gap between suggested dates
  if (suggestedIn && suggestedOut) {
    const gap = suggestedOut.getTime() - suggestedIn.getTime();
    if (gap < MIN_SUGGESTED_DAYS * 86400000) {
      suggestedOut = new Date(
        suggestedIn.getTime() + MIN_SUGGESTED_DAYS * 86400000
      );
    }
  }

  return { suggestedIn, suggestedOut };
}

// ─── Cell Component ──────────────────────────────────────────────────────────

function GrazingEventCell({
  cell,
  onSaveEdit,
  onAddEvent,
  onDelete,
  isAddingEvent,
  isJustEdited,
  grid,
  fieldIndex,
  rotationIndex,
}: {
  cell: GridCell;
  onSaveEdit: (
    cell: GridCell,
    values: {
      dateIn: string;
      dateOut: string;
      startDryMatter: number | null;
      endDryMatter: number | null;
    }
  ) => void | Promise<void>;
  onAddEvent: (
    cell: GridCell,
    suggestion: { suggestedIn: Date | null; suggestedOut: Date | null },
    biomass: { startDryMatter: number | null; endDryMatter: number | null }
  ) => void | Promise<void>;
  onDelete: (cell: GridCell) => void;
  isAddingEvent: boolean;
  isJustEdited: boolean;
  grid: GridCell[][];
  fieldIndex: number;
  rotationIndex: number;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [localDateIn, setLocalDateIn] = useState("");
  const [localDateOut, setLocalDateOut] = useState("");
  const [localTimeIn, setLocalTimeIn] = useState("");
  const [localTimeOut, setLocalTimeOut] = useState("");
  const [showTime, setShowTime] = useState(false);
  const [localStartDryMatter, setLocalStartDryMatter] = useState("");
  const [localEndDryMatter, setLocalEndDryMatter] = useState("");
  const [saveDateErrors, setSaveDateErrors] = useState<GrazingEditDateErrors>({
    dateIn: false,
    dateOut: false,
  });
  const [addBiomassErrors, setAddBiomassErrors] = useState({
    pre: false,
    post: false,
  });
  const mobileEditPopoverAnchorPosition =
    typeof window === "undefined"
      ? { top: 0, left: 0 }
      : {
          top: Math.round(window.innerHeight * 0.45),
          left: Math.round(window.innerWidth / 2),
        };

  const effectiveStatus = cell.status;
  const isUnattached =
    cell.moveEvent?.animalGroupGuid == null && !!cell.moveEvent;
  const isCurrentOpen =
    cell.moveEvent != null && cell.moveEvent.dateTimeOut == null;
  const colors = effectiveStatus ? STATUS_COLORS[effectiveStatus] : null;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setShowTime(false);
    setSaveDateErrors({ dateIn: false, dateOut: false });
    setAddBiomassErrors({ pre: false, post: false });
    if (cell.moveEvent?.dateTimeIn) {
      const inIso = new Date(cell.moveEvent.dateTimeIn).toISOString();
      setLocalDateIn(inIso.slice(0, 10));
      setLocalTimeIn(inIso.slice(11, 16));
    } else {
      setLocalDateIn("");
      setLocalTimeIn("");
    }
    if (cell.dateOut) {
      const outIso = new Date(cell.dateOut).toISOString();
      setLocalDateOut(outIso.slice(0, 10));
      setLocalTimeOut(outIso.slice(11, 16));
    } else {
      setLocalDateOut("");
      setLocalTimeOut("");
    }
    setLocalStartDryMatter(
      cell.moveEvent?.startDryMatter != null
        ? String(Math.round(cell.moveEvent.startDryMatter))
        : ""
    );
    setLocalEndDryMatter(
      cell.moveEvent?.endDryMatter != null
        ? String(Math.round(cell.moveEvent.endDryMatter))
        : ""
    );
  };

  const OriginIcon = cell.isShed
    ? HomeIcon
    : cell.origin === "machine"
    ? SatelliteAltIcon
    : PersonIcon;

  const sessionDays = useMemo(() => {
    if (!cell.dateIn) return null;
    if (!cell.dateOut) return null; // ongoing — don't show inflated day count
    return getDaysBetween(cell.dateIn, cell.dateOut);
  }, [cell.dateIn, cell.dateOut]);

  const preCover = cell.moveEvent?.startDryMatter ?? null;
  const postCover = cell.moveEvent?.endDryMatter ?? null;
  const missingMoveIn = isMissingMoveInEvent(cell.moveEvent);
  const coverWarning = getCoverWarning(cell.moveEvent);
  const warning = [coverWarning, cell.overlapWarning]
    .filter(Boolean)
    .join("\n\n");
  const cellWarning = warning.length > 0 ? warning : null;
  const dateOrderInvalid = isGrazingEditDateOrderInvalid(
    localDateIn,
    localTimeIn,
    localDateOut,
    localTimeOut,
    showTime
  );
  const sameGrazingDay = Boolean(
    localDateIn && localDateOut && localDateIn === localDateOut
  );
  const grazingEditFields: GrazingEditDateTimeFields = {
    dateIn: localDateIn,
    timeIn: localTimeIn,
    dateOut: localDateOut,
    timeOut: localTimeOut,
  };
  const applyGrazingEditFields = (next: GrazingEditDateTimeFields) => {
    setLocalDateIn(next.dateIn);
    setLocalTimeIn(next.timeIn);
    setLocalDateOut(next.dateOut);
    setLocalTimeOut(next.timeOut);
    if (next.dateIn) {
      setSaveDateErrors((prev) => ({ ...prev, dateIn: false }));
    }
    if (next.dateOut) {
      setSaveDateErrors((prev) => ({ ...prev, dateOut: false }));
    }
  };

  const isEmpty = cell.status === null;

  // For empty cells, compute suggestion on demand
  const suggestion = useMemo(() => {
    if (!isEmpty) return { suggestedIn: null, suggestedOut: null };
    return suggestEventDates(grid, fieldIndex, rotationIndex);
  }, [isEmpty, grid, fieldIndex, rotationIndex]);

  const hasSuggestion = suggestion.suggestedIn !== null;

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          width: "100%",
          minHeight: 44,
          borderRadius: 1.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
          backgroundColor: colors ? colors.bg : "transparent",
          border: colors
            ? `${isCurrentOpen ? "2.5px" : "1.5px"} ${
                isUnattached ? "dashed" : "solid"
              } ${isCurrentOpen ? COLORS.dahlia[700] : colors.border}`
            : "1.5px dashed #E0E0E0",
          boxShadow: isCurrentOpen
            ? `0 0 0 2px ${COLORS.bellflower[300]}`
            : "none",
          opacity: isUnattached ? 0.6 : 1,
          fontStyle: isUnattached ? "italic" : "normal",
          position: "relative",
          px: 0.75,
          py: cell.status ? 0.5 : 0,
          "&:hover": isEmpty
            ? {
                borderColor: "#BDBDBD",
                backgroundColor: "#FAFAFA",
                "& .add-hint": { opacity: 1 },
              }
            : {
                filter: "brightness(0.95)",
                transform: "scale(1.02)",
              },
          ...(isJustEdited && {
            animation: "editPulse 2s ease-out",
            "@keyframes editPulse": {
              "0%": {
                boxShadow: `0 0 0 4px ${COLORS.bellflower[500]}`,
                transform: "scale(1.04)",
              },
              "30%": {
                boxShadow: `0 0 0 3px ${COLORS.bellflower[300]}`,
                transform: "scale(1)",
              },
              "100%": { boxShadow: "0 0 0 0 transparent" },
            },
          }),
          ...(missingMoveIn && {
            boxShadow: `inset 0 0 0 2px ${COLORS.daisy[500]}`,
          }),
        }}
      >
        {missingMoveIn && (
          <Tooltip title={t("rotationGrid.missingMoveIn")} placement="top">
            <WarningAmberIcon
              sx={{
                position: "absolute",
                top: -6,
                right: -6,
                fontSize: 16,
                color: COLORS.daisy[700],
                backgroundColor: "white",
                borderRadius: "50%",
                zIndex: 1,
              }}
            />
          </Tooltip>
        )}
        {cellWarning && (
          <Tooltip
            title={
              <span style={{ whiteSpace: "pre-line" }}>{cellWarning}</span>
            }
            placement="top"
          >
            <ErrorOutlineIcon
              sx={{
                position: "absolute",
                top: -6,
                left: -6,
                fontSize: 16,
                color: "#D32F2F",
                backgroundColor: "white",
                borderRadius: "50%",
                zIndex: 1,
              }}
            />
          </Tooltip>
        )}

        {isUnattached && (
          <Tooltip
            title={
              <Box sx={{ maxWidth: 240 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
                >
                  {t("rotationGrid.unattachedTitle")}
                </Typography>
                <Typography variant="caption" sx={{ display: "block" }}>
                  {t(
                    cell.origin === "machine"
                      ? "rotationGrid.unattachedExplanationMachine"
                      : "rotationGrid.unattachedExplanation"
                  )}
                </Typography>
              </Box>
            }
            placement="top"
          >
            <HelpOutlineIcon
              sx={{
                position: "absolute",
                top: -6,
                right: -6,
                fontSize: 16,
                color: COLORS.bellflower[700],
                backgroundColor: "white",
                borderRadius: "50%",
                zIndex: 1,
              }}
            />
          </Tooltip>
        )}

        {isCurrentOpen && (
          <Tooltip
            title={
              <Box sx={{ maxWidth: 240 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
                >
                  {t("rotationGrid.currentOpenTitle")}
                </Typography>
                <Typography variant="caption" sx={{ display: "block" }}>
                  {t("rotationGrid.currentOpenExplanation")}
                </Typography>
              </Box>
            }
            placement="top"
          >
            <HelpOutlineIcon
              sx={{
                position: "absolute",
                bottom: -6,
                right: -6,
                fontSize: 16,
                color: COLORS.dahlia[700],
                backgroundColor: "white",
                borderRadius: "50%",
                zIndex: 1,
              }}
            />
          </Tooltip>
        )}

        {cell.status !== null ? (
          <>
            {/* Field name + date row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                width: "100%",
              }}
            >
              <OriginIcon
                sx={{
                  fontSize: 12,
                  color: colors?.text,
                  opacity: 0.7,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.65rem",
                  color: colors?.text,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flex: 1,
                }}
              >
                {cell.fieldName}
              </Typography>
              {(cell.dateIn ?? cell.dateOut) && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.6rem",
                    color: colors?.text,
                    fontWeight: 500,
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    opacity: 0.8,
                  }}
                >
                  {new Date(
                    (cell.dateIn ?? cell.dateOut) as string
                  ).toLocaleDateString(undefined, {
                    day: "numeric",
                    month: "short",
                  })}
                </Typography>
              )}
            </Box>
            {/* Session length */}
            {sessionDays != null && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.55rem",
                  color: colors?.text,
                  fontWeight: 400,
                  opacity: 0.8,
                  lineHeight: 1.2,
                }}
              >
                {sessionDays}d
              </Typography>
            )}
            {/* Pre/post cover */}
            {(preCover != null || postCover != null) && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.5rem",
                  color: colors?.text,
                  opacity: 0.7,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}
              >
                {preCover != null ? Math.round(preCover) : "—"}
                {" → "}
                {postCover != null ? Math.round(postCover) : "—"}
              </Typography>
            )}
          </>
        ) : (
          /* Empty cell: show add hint on hover */
          <AddCircleOutlineIcon
            className="add-hint"
            sx={{
              fontSize: 18,
              color: "#BDBDBD",
              opacity: 0,
              transition: "opacity 0.2s",
            }}
          />
        )}
      </Box>

      {/* Popover for filled cells */}
      {cell.status !== null && (
        <Popover
          open={Boolean(anchorEl)}
          anchorReference={isMobile ? "anchorPosition" : "anchorEl"}
          anchorPosition={
            isMobile ? mobileEditPopoverAnchorPosition : undefined
          }
          anchorEl={isMobile ? undefined : anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: isMobile ? "center" : "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: isMobile ? "center" : "top",
            horizontal: "center",
          }}
          marginThreshold={isMobile ? 16 : undefined}
          slotProps={{
            paper: {
              sx: {
                borderRadius: 2,
                p: 2,
                minWidth: 220,
                width: { xs: "min(calc(100vw - 48px), 360px)", sm: "auto" },
                maxWidth: { xs: "calc(100vw - 48px)", sm: 300 },
                maxHeight: {
                  xs: "calc(100dvh - 180px)",
                  sm: "calc(100dvh - 96px)",
                },
                overflowY: "auto",
              },
            },
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {cell.fieldName}
          </Typography>
          {missingMoveIn && cell.dateOut ? (
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block" }}
            >
              — → {new Date(cell.dateOut).toLocaleDateString()}
            </Typography>
          ) : (
            cell.dateIn && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block" }}
              >
                {new Date(cell.dateIn).toLocaleDateString()}
                {cell.dateOut
                  ? ` → ${new Date(cell.dateOut).toLocaleDateString()}`
                  : ` → ${t("rotationTimeline.current")}`}
                {sessionDays != null && ` (${sessionDays}d)`}
              </Typography>
            )
          )}

          {/* Pre/post cover if available */}
          {(preCover != null || postCover != null) && (
            <Typography
              variant="caption"
              sx={{ display: "block", color: "text.secondary", mt: 0.5 }}
            >
              {preCover != null &&
                `${t("rotationGrid.preCover")}: ${Math.round(preCover)} kg/ha`}
              {preCover != null && postCover != null && " · "}
              {postCover != null &&
                `${t("rotationGrid.postCover")}: ${Math.round(
                  postCover
                )} kg/ha`}
            </Typography>
          )}

          {/* Edit form */}
          <Box
            sx={{
              mt: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                label={t("rotationGrid.editDateIn")}
                type="date"
                size="small"
                value={localDateIn}
                onChange={(e) =>
                  applyGrazingEditFields(
                    clampGrazingEditAfterDateInChange(
                      grazingEditFields,
                      e.target.value,
                      showTime
                    )
                  )
                }
                error={dateOrderInvalid || saveDateErrors.dateIn}
                helperText={
                  saveDateErrors.dateIn
                    ? parseDryMatter(localStartDryMatter) != null
                      ? t("rotationGrid.dateRequiredForPreCover")
                      : t("rotationGrid.dateInRequired")
                    : undefined
                }
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: localDateOut ? { max: localDateOut } : {},
                }}
                fullWidth
              />
              {showTime && (
                <TextField
                  type="time"
                  size="small"
                  value={localTimeIn}
                  onChange={(e) =>
                    applyGrazingEditFields(
                      clampGrazingEditAfterTimeInChange(
                        grazingEditFields,
                        e.target.value,
                        showTime
                      )
                    )
                  }
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput:
                      sameGrazingDay && localTimeOut
                        ? { max: localTimeOut }
                        : {},
                  }}
                  sx={{ minWidth: 90 }}
                />
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                label={t("rotationGrid.editDateOut")}
                type="date"
                size="small"
                value={localDateOut}
                onChange={(e) =>
                  applyGrazingEditFields(
                    clampGrazingEditAfterDateOutChange(
                      grazingEditFields,
                      e.target.value,
                      showTime
                    )
                  )
                }
                error={dateOrderInvalid || saveDateErrors.dateOut}
                helperText={
                  dateOrderInvalid
                    ? t("rotationGrid.dateOrderError")
                    : saveDateErrors.dateOut
                    ? t("rotationGrid.dateRequiredForPostCover")
                    : undefined
                }
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: localDateIn ? { min: localDateIn } : {},
                }}
                fullWidth
              />
              {showTime && (
                <TextField
                  type="time"
                  size="small"
                  value={localTimeOut}
                  onChange={(e) =>
                    applyGrazingEditFields(
                      clampGrazingEditAfterTimeOutChange(
                        grazingEditFields,
                        e.target.value,
                        showTime
                      )
                    )
                  }
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput:
                      sameGrazingDay && localTimeIn ? { min: localTimeIn } : {},
                  }}
                  sx={{ minWidth: 90 }}
                />
              )}
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={
                <AccessTimeIcon sx={{ fontSize: "14px !important" }} />
              }
              onClick={() => {
                const nextShowTime = !showTime;
                setShowTime(nextShowTime);
                if (
                  nextShowTime &&
                  localDateIn &&
                  localDateOut &&
                  localDateIn === localDateOut
                ) {
                  if (localTimeIn && !localTimeOut) {
                    setLocalTimeOut(localTimeIn);
                  } else if (!localTimeIn && localTimeOut) {
                    setLocalTimeIn(localTimeOut);
                  } else if (
                    localTimeIn &&
                    localTimeOut &&
                    localTimeOut < localTimeIn
                  ) {
                    setLocalTimeOut(localTimeIn);
                  }
                }
              }}
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                alignSelf: "flex-start",
                py: 0.25,
                borderColor: COLORS.bellflower[300],
                color: COLORS.bellflower[700],
                "&:hover": {
                  borderColor: COLORS.bellflower[500],
                  backgroundColor: COLORS.bellflower[50],
                },
              }}
            >
              {showTime
                ? t("rotationGrid.hideTime")
                : t("rotationGrid.editTime")}
            </Button>
            {!cell.isShed && (
              <>
                <TextField
                  label={t("rotationGrid.preCover") + " (kg/ha)"}
                  type="number"
                  size="small"
                  value={localStartDryMatter}
                  onChange={(e) => setLocalStartDryMatter(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  fullWidth
                />
                <TextField
                  label={t("rotationGrid.postCover") + " (kg/ha)"}
                  type="number"
                  size="small"
                  value={localEndDryMatter}
                  onChange={(e) => setLocalEndDryMatter(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  fullWidth
                />
              </>
            )}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                mx: -2,
                mb: -2,
                mt: 0.5,
                px: 2,
                py: 1.5,
                backgroundColor: "background.paper",
                borderTop: "1px solid",
                borderColor: "divider",
                zIndex: 1,
              }}
            >
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={isGrazingEventSaveDisabled(dateOrderInvalid)}
                onClick={() => {
                  const dateErrors = getGrazingEditDateErrors(
                    { dateIn: localDateIn, dateOut: localDateOut },
                    {
                      startDryMatter: localStartDryMatter,
                      endDryMatter: localEndDryMatter,
                    }
                  );
                  if (hasGrazingEditDateErrors(dateErrors)) {
                    setSaveDateErrors(dateErrors);
                    return;
                  }
                  setSaveDateErrors({ dateIn: false, dateOut: false });
                  const { dateIn, dateOut } = buildGrazingEditIsoPair(
                    {
                      dateIn: localDateIn,
                      timeIn: localTimeIn,
                      dateOut: localDateOut,
                      timeOut: localTimeOut,
                      showTime,
                    },
                    {
                      dateTimeIn: cell.moveEvent?.dateTimeIn,
                      dateTimeOut: cell.dateOut,
                    }
                  );
                  void (async () => {
                    try {
                      await onSaveEdit(cell, {
                        dateIn,
                        dateOut: dateOut ?? "",
                        ...parseBiomassForGrazingDates(
                          { dateIn: localDateIn, dateOut: localDateOut },
                          {
                            startDryMatter: localStartDryMatter,
                            endDryMatter: localEndDryMatter,
                          }
                        ),
                      });
                      setAnchorEl(null);
                    } catch {
                      // Stay open on offline guard or save failure.
                    }
                  })();
                }}
                sx={{ textTransform: "none", flex: 1 }}
              >
                {cell.status === "proposed"
                  ? "Accept"
                  : t("settings.button.save")}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setAnchorEl(null)}
                sx={{ textTransform: "none" }}
              >
                {t("settings.button.close")}
              </Button>
              {cell.moveEvent && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={() => {
                    onDelete(cell);
                    setAnchorEl(null);
                  }}
                  sx={{ textTransform: "none" }}
                >
                  {t("rotationGrid.delete")}
                </Button>
              )}
            </Box>
          </Box>
        </Popover>
      )}

      {/* Popover for empty cells — add event */}
      {isEmpty && (
        <Popover
          open={Boolean(anchorEl)}
          anchorReference={isMobile ? "anchorPosition" : "anchorEl"}
          anchorPosition={
            isMobile ? mobileEditPopoverAnchorPosition : undefined
          }
          anchorEl={isMobile ? undefined : anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: isMobile ? "center" : "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: isMobile ? "center" : "top",
            horizontal: "center",
          }}
          marginThreshold={isMobile ? 16 : undefined}
          slotProps={{
            paper: {
              sx: {
                borderRadius: 2,
                p: 2,
                minWidth: 220,
                width: { xs: "min(calc(100vw - 48px), 360px)", sm: "auto" },
                maxWidth: { xs: "calc(100vw - 48px)", sm: 280 },
                maxHeight: {
                  xs: "calc(100dvh - 180px)",
                  sm: "calc(100dvh - 96px)",
                },
                overflowY: "auto",
              },
            },
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {cell.fieldName}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mb: 1.5 }}
          >
            {t("rotationGrid.addEventHint")}
          </Typography>

          {hasSuggestion && (
            <Box
              sx={{
                mb: 1.5,
                px: 1,
                py: 0.75,
                borderRadius: 1,
                backgroundColor: "#F3E5F5",
                border: "1px solid #CE93D8",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "#6A1B9A", fontWeight: 500, display: "block" }}
              >
                {t("rotationGrid.suggestedDates")}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#6A1B9A", display: "block" }}
              >
                {suggestion.suggestedIn?.toLocaleDateString(undefined, {
                  day: "numeric",
                  month: "short",
                })}
                {" → "}
                {suggestion.suggestedOut?.toLocaleDateString(undefined, {
                  day: "numeric",
                  month: "short",
                })}
              </Typography>
            </Box>
          )}

          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 1.5 }}
          >
            <TextField
              label={t("rotationGrid.preCover") + " (kg/ha)"}
              type="number"
              size="small"
              value={localStartDryMatter}
              onChange={(e) => {
                setLocalStartDryMatter(e.target.value);
                if (!parseDryMatter(e.target.value)) {
                  setAddBiomassErrors((prev) => ({ ...prev, pre: false }));
                }
              }}
              error={addBiomassErrors.pre}
              helperText={
                addBiomassErrors.pre
                  ? t("rotationGrid.dateRequiredForPreCover")
                  : undefined
              }
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
            <TextField
              label={t("rotationGrid.postCover") + " (kg/ha)"}
              type="number"
              size="small"
              value={localEndDryMatter}
              onChange={(e) => {
                setLocalEndDryMatter(e.target.value);
                if (!parseDryMatter(e.target.value)) {
                  setAddBiomassErrors((prev) => ({ ...prev, post: false }));
                }
              }}
              error={addBiomassErrors.post}
              helperText={
                addBiomassErrors.post
                  ? t("rotationGrid.dateRequiredForPostCover")
                  : undefined
              }
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
          </Box>

          <Box
            sx={{
              mx: -2,
              mb: -2,
              mt: 0.5,
              px: 2,
              py: 1.5,
              backgroundColor: "background.paper",
              borderTop: "1px solid",
              borderColor: "divider",
              zIndex: 1,
            }}
          >
            <Button
              size="small"
              variant="contained"
              color="primary"
              fullWidth
              disabled={isAddingEvent}
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => {
                const suggestedDateIn = suggestion.suggestedIn
                  ? suggestion.suggestedIn.toISOString().slice(0, 10)
                  : "";
                const suggestedDateOut = suggestion.suggestedOut
                  ? suggestion.suggestedOut.toISOString().slice(0, 10)
                  : "";
                const dateErrors = getGrazingEditDateErrors(
                  { dateIn: suggestedDateIn, dateOut: suggestedDateOut },
                  {
                    startDryMatter: localStartDryMatter,
                    endDryMatter: localEndDryMatter,
                  },
                  { requireDateIn: false }
                );
                if (hasGrazingEditDateErrors(dateErrors)) {
                  setAddBiomassErrors({
                    pre: dateErrors.dateIn,
                    post: dateErrors.dateOut,
                  });
                  return;
                }
                setAddBiomassErrors({ pre: false, post: false });
                void (async () => {
                  try {
                    await onAddEvent(
                      cell,
                      suggestion,
                      parseBiomassForGrazingDates(
                        { dateIn: suggestedDateIn, dateOut: suggestedDateOut },
                        {
                          startDryMatter: localStartDryMatter,
                          endDryMatter: localEndDryMatter,
                        }
                      )
                    );
                    setAnchorEl(null);
                  } catch {
                    // Stay open on offline guard or save failure.
                  }
                })();
              }}
              sx={{ textTransform: "none" }}
            >
              {hasSuggestion
                ? t("rotationGrid.addSuggested")
                : t("rotationGrid.addEvent")}
            </Button>
          </Box>
        </Popover>
      )}
    </>
  );
}

// ─── Review Panel ────────────────────────────────────────────────────────────

const dateFmt: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };

function ReviewPanel({
  analysis,
  grid,
  onAccept,
  onDelete,
  onResolved,
}: {
  analysis: RotationAnalysis | null;
  grid: GridCell[][];
  onAccept: (cell: GridCell) => void;
  onDelete: (cell: GridCell) => void;
  onResolved: () => void;
}) {
  const { t } = useTranslation();
  const [dismissedProposals, setDismissedProposals] = useState<Set<string>>(
    new Set()
  );
  const [acceptedProposals, setAcceptedProposals] = useState<Set<string>>(
    new Set()
  );
  const [celebrating, setCelebrating] = useState(false);
  const [expanded, setExpanded] = useState(true);

  // Find proposed cells from the grid
  const proposedCells = useMemo(() => {
    const cells: GridCell[] = [];
    for (const row of grid) {
      for (const cell of row) {
        if (cell.status === "proposed" && cell.origin === "machine") {
          cells.push(cell);
        }
      }
    }
    return cells;
  }, [grid]);

  const visibleProposals = proposedCells.filter(
    (c) =>
      !dismissedProposals.has(c.moveEvent?.dateTimeIn ?? "") &&
      !acceptedProposals.has(c.moveEvent?.dateTimeIn ?? "")
  );

  const totalItems = proposedCells.length;
  const completedItems = acceptedProposals.size + dismissedProposals.size;
  const progress = totalItems > 0 ? completedItems / totalItems : 0;
  const allDone = totalItems > 0 && completedItems >= totalItems;

  // Trigger celebration when all done
  useEffect(() => {
    if (allDone && !celebrating) {
      setCelebrating(true);
      onResolved();
      const timer = setTimeout(() => setCelebrating(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [allDone, celebrating, onResolved]);

  if (!analysis) return null;
  if (totalItems === 0) return null;

  if (celebrating) {
    return (
      <Box
        sx={{
          mb: 2,
          px: 2,
          py: 2.5,
          borderRadius: 2,
          backgroundColor: COLORS.dahlia[50],
          border: `1px solid ${COLORS.dahlia[300]}`,
          textAlign: "center",
          animation: "celebrateIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
          "@keyframes celebrateIn": {
            "0%": { transform: "scale(0.8)", opacity: 0 },
            "60%": { transform: "scale(1.04)" },
            "100%": { transform: "scale(1)", opacity: 1 },
          },
        }}
      >
        <Typography
          sx={{
            fontSize: "1.5rem",
            mb: 0.5,
            animation: "popIn 0.4s ease-out 0.2s both",
            "@keyframes popIn": {
              "0%": { transform: "scale(0)", opacity: 0 },
              "100%": { transform: "scale(1)", opacity: 1 },
            },
          }}
        >
          🎉
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: COLORS.dahlia[900],
            fontSize: "0.95rem",
          }}
        >
          {t("review.allDone")}
        </Typography>
        <Typography variant="caption" sx={{ color: COLORS.dahlia[700] }}>
          {t("review.allDoneDetail")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mb: 2,
        borderRadius: 2,
        border: `1px solid ${COLORS.bellflower[300]}`,
        backgroundColor: COLORS.bellflower[50],
        overflow: "hidden",
      }}
    >
      {/* Header — clickable to expand/collapse */}
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          px: 2,
          py: 1.25,
          backgroundColor: COLORS.bellflower[100],
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          userSelect: "none",
          "&:hover": { backgroundColor: COLORS.bellflower[300] + "40" },
          transition: "background-color 0.15s",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: COLORS.bellflower[700],
              transition: "transform 0.2s",
              transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
            }}
          >
            ▶
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: COLORS.bellflower[900] }}
          >
            {t("review.title")}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: COLORS.bellflower[700],
              backgroundColor: COLORS.bellflower[300],
              borderRadius: 10,
              px: 1,
              py: 0.1,
              fontWeight: 600,
              fontSize: "0.65rem",
            }}
          >
            {totalItems - completedItems}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: COLORS.bellflower[700] }}>
          {completedItems}/{totalItems}
        </Typography>
      </Box>

      {/* Progress bar */}
      <Box
        sx={{
          height: 3,
          backgroundColor: COLORS.bellflower[100],
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${progress * 100}%`,
            backgroundColor: COLORS.dahlia[500],
            transition: "width 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
            borderRadius: "0 2px 2px 0",
          }}
        />
      </Box>

      {/* Collapsible content */}
      <Box
        sx={{
          maxHeight: expanded ? 600 : 0,
          overflow: "hidden",
          transition: "max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {/* Proposed events section */}
          {visibleProposals.map((cell, i) => {
            const key = cell.moveEvent?.dateTimeIn ?? String(i);
            return (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1,
                  borderRadius: 1.5,
                  backgroundColor: "white",
                  border: `1px solid ${COLORS.bellflower[300]}`,
                  animation: "slideIn 0.3s ease-out",
                  "@keyframes slideIn": {
                    "0%": { opacity: 0, transform: "translateX(-8px)" },
                    "100%": { opacity: 1, transform: "translateX(0)" },
                  },
                }}
              >
                <SatelliteAltIcon
                  sx={{
                    fontSize: 16,
                    color: COLORS.bellflower[500],
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: COLORS.bellflower[900],
                      fontSize: "0.8rem",
                    }}
                  >
                    {cell.fieldName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: COLORS.bellflower[700], fontSize: "0.7rem" }}
                  >
                    {cell.dateIn &&
                      new Date(cell.dateIn).toLocaleDateString(
                        undefined,
                        dateFmt
                      )}
                    {cell.dateOut &&
                      ` — ${new Date(cell.dateOut).toLocaleDateString(
                        undefined,
                        dateFmt
                      )}`}
                    {!cell.dateOut && ` — ${t("rotationGrid.ongoing")}`}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      onAccept(cell);
                      setAcceptedProposals((prev) => new Set([...prev, key]));
                    }}
                    sx={{
                      minWidth: 0,
                      px: 1.5,
                      py: 0.25,
                      textTransform: "none",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      backgroundColor: COLORS.dahlia[700],
                      color: "white",
                      "&:hover": { backgroundColor: COLORS.dahlia[900] },
                      borderRadius: 1.5,
                    }}
                    startIcon={
                      <CheckCircleIcon sx={{ fontSize: "14px !important" }} />
                    }
                  >
                    {t("review.looksRight")}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      onDelete(cell);
                      setDismissedProposals((prev) => new Set([...prev, key]));
                    }}
                    sx={{
                      minWidth: 0,
                      px: 1,
                      py: 0.25,
                      textTransform: "none",
                      fontSize: "0.75rem",
                      borderColor: COLORS.bellflower[300],
                      color: COLORS.bellflower[700],
                      "&:hover": {
                        borderColor: COLORS.bellflower[500],
                        backgroundColor: COLORS.bellflower[100],
                      },
                      borderRadius: 1.5,
                    }}
                  >
                    {t("review.notRight")}
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      {/* end collapsible content */}
    </Box>
  );
}

// ─── Add Missing Event Picker ────────────────────────────────────────────────

function AddMissingEventPicker({
  fields,
  lastEventEnd,
  animalGroupName,
  onAdd,
  onMoveToShed,
}: {
  fields: MissingField[];
  lastEventEnd: string | null;
  animalGroupName?: string;
  onAdd: (
    fieldGuid: string,
    dateIn: string,
    dateOut: string | null,
    biomass: { startDryMatter: number | null; endDryMatter: number | null }
  ) => void | Promise<void>;
  onMoveToShed?: () => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedField, setSelectedField] = useState<MissingField | null>(null);
  const [dateIn, setDateIn] = useState("");
  const [dateOut, setDateOut] = useState("");
  const [startDryMatter, setStartDryMatter] = useState("");
  const [endDryMatter, setEndDryMatter] = useState("");
  const [saveDateErrors, setSaveDateErrors] = useState<GrazingEditDateErrors>({
    dateIn: false,
    dateOut: false,
  });
  const mobilePopoverAnchorPosition =
    typeof window === "undefined"
      ? { top: 0, left: 0 }
      : {
          top: Math.round(window.innerHeight * 0.45),
          left: Math.round(window.innerWidth / 2),
        };
  const dateOrderInvalid = Boolean(dateIn && dateOut && dateOut < dateIn);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setSelectedField(null);
    setSaveDateErrors({ dateIn: false, dateOut: false });
    // Default dateIn to day after last event
    if (lastEventEnd) {
      const next = new Date(new Date(lastEventEnd).getTime() + 86400000);
      setDateIn(next.toISOString().slice(0, 10));
    } else {
      setDateIn("");
    }
    setDateOut("");
    setStartDryMatter("");
    setEndDryMatter("");
  };

  const handleSubmit = async () => {
    if (!selectedField) return;
    const dateErrors = getGrazingEditDateErrors(
      { dateIn, dateOut },
      { startDryMatter, endDryMatter }
    );
    if (hasGrazingEditDateErrors(dateErrors) || dateOrderInvalid) {
      setSaveDateErrors(dateErrors);
      return;
    }
    setSaveDateErrors({ dateIn: false, dateOut: false });
    const din = new Date(dateIn + "T12:00:00Z").toISOString();
    const dout = dateOut
      ? new Date(dateOut + "T12:00:00Z").toISOString()
      : null;
    try {
      await onAdd(selectedField.guid, din, dout, {
        ...parseBiomassForGrazingDates(
          { dateIn, dateOut },
          { startDryMatter, endDryMatter }
        ),
      });
      setAnchorEl(null);
    } catch {
      // Stay open on offline guard or save failure.
    }
  };

  return (
    <>
      <Box
        sx={{
          mt: 0.5,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {/* Add button */}
        <Button
          size="small"
          variant="outlined"
          startIcon={
            <AddCircleOutlineIcon sx={{ fontSize: "14px !important" }} />
          }
          onClick={handleOpen}
          sx={{
            textTransform: "none",
            fontSize: "0.65rem",
            fontWeight: 500,
            color: COLORS.bellflower[700],
            borderColor: COLORS.bellflower[300],
            borderStyle: "dashed",
            borderRadius: 1.5,
            py: 0.5,
            "&:hover": {
              borderColor: COLORS.bellflower[500],
              backgroundColor: COLORS.bellflower[50],
              borderStyle: "dashed",
            },
          }}
        >
          {t("rotationGrid.addEvent")}
        </Button>
        {onMoveToShed && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<HomeIcon sx={{ fontSize: "14px !important" }} />}
            onClick={onMoveToShed}
            sx={{
              textTransform: "none",
              fontSize: "0.65rem",
              fontWeight: 500,
              color: COLORS.bellflower[700],
              borderColor: COLORS.bellflower[300],
              borderStyle: "dashed",
              borderRadius: 1.5,
              py: 0.5,
              "&:hover": {
                borderColor: COLORS.bellflower[500],
                backgroundColor: COLORS.bellflower[50],
                borderStyle: "dashed",
              },
            }}
          >
            {t("animalGroup.moveToShed")}
          </Button>
        )}
      </Box>

      {/* Field picker popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorReference={isMobile ? "anchorPosition" : "anchorEl"}
        anchorPosition={isMobile ? mobilePopoverAnchorPosition : undefined}
        anchorEl={isMobile ? undefined : anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: isMobile ? "center" : "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: isMobile ? "center" : "top",
          horizontal: "center",
        }}
        marginThreshold={isMobile ? 16 : undefined}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              p: 2,
              minWidth: 220,
              width: { xs: "min(calc(100vw - 48px), 360px)", sm: "auto" },
              maxWidth: { xs: "calc(100vw - 48px)", sm: 280 },
              maxHeight: {
                xs: "calc(100dvh - 180px)",
                sm: "calc(100dvh - 96px)",
              },
              overflowY: "auto",
            },
          },
        }}
      >
        {!selectedField ? (
          <>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1, fontSize: "0.8rem" }}
            >
              {t("review.pickField")}
            </Typography>
            {(() => {
              const sortByName = (a: MissingField, b: MissingField) =>
                a.name.localeCompare(b.name);
              const assigned = fields
                .filter((f) => f.isAssigned)
                .sort(sortByName);
              const others = fields
                .filter((f) => !f.isAssigned)
                .sort(sortByName);
              const renderButton = (f: MissingField) => (
                <Button
                  key={f.guid}
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedField(f)}
                  sx={{
                    textTransform: "none",
                    justifyContent: "flex-start",
                    textAlign: "left",
                    fontWeight: 500,
                    fontSize: "0.8rem",
                    fontStyle: f.isAssigned ? "normal" : "italic",
                    borderColor: f.isAssigned
                      ? COLORS.dahlia[300]
                      : COLORS.dahlia[100],
                    color: f.isAssigned
                      ? COLORS.dahlia[900]
                      : COLORS.dahlia[500],
                    "&:hover": {
                      backgroundColor: COLORS.dahlia[50],
                      borderColor: f.isAssigned
                        ? COLORS.dahlia[500]
                        : COLORS.dahlia[300],
                    },
                    borderRadius: 1.5,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      flex: 1,
                      textAlign: "left",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {f.name}
                  </Box>
                </Button>
              );
              const renderSectionHeader = (label: string) => (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    mt: 0.5,
                  }}
                >
                  {label}
                </Typography>
              );
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  {assigned.length > 0 && (
                    <>
                      {renderSectionHeader(
                        animalGroupName
                          ? t("rotationGrid.assignedTo", {
                              name: animalGroupName,
                            })
                          : t("rotationGrid.assignedFields")
                      )}
                      {assigned.map(renderButton)}
                    </>
                  )}
                  {others.length > 0 && (
                    <>
                      {renderSectionHeader(t("rotationGrid.otherFields"))}
                      {others.map(renderButton)}
                    </>
                  )}
                </Box>
              );
            })()}
          </>
        ) : (
          <>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 0.5, fontSize: "0.8rem" }}
            >
              {selectedField.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block", mb: 1.5 }}
            >
              {t("review.enterDates")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <TextField
                label={t("rotationGrid.editDateIn")}
                type="date"
                size="small"
                value={dateIn}
                onChange={(e) => {
                  const next = e.target.value;
                  setDateIn(next);
                  if (next) {
                    setSaveDateErrors((prev) => ({ ...prev, dateIn: false }));
                  }
                }}
                error={dateOrderInvalid || saveDateErrors.dateIn}
                helperText={
                  saveDateErrors.dateIn
                    ? parseDryMatter(startDryMatter) != null
                      ? t("rotationGrid.dateRequiredForPreCover")
                      : t("rotationGrid.dateInRequired")
                    : undefined
                }
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: dateOut ? { max: dateOut } : {},
                }}
                fullWidth
              />
              <TextField
                label={t("rotationGrid.editDateOut")}
                type="date"
                size="small"
                value={dateOut}
                onChange={(e) => {
                  const next = e.target.value;
                  setDateOut(next);
                  if (next) {
                    setSaveDateErrors((prev) => ({ ...prev, dateOut: false }));
                  }
                }}
                error={dateOrderInvalid || saveDateErrors.dateOut}
                helperText={
                  dateOrderInvalid
                    ? t("rotationGrid.dateOrderError")
                    : saveDateErrors.dateOut
                    ? t("rotationGrid.dateRequiredForPostCover")
                    : undefined
                }
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: dateIn ? { min: dateIn } : {},
                }}
                fullWidth
              />
              <TextField
                label={t("rotationGrid.preCover") + " (kg/ha)"}
                type="number"
                size="small"
                value={startDryMatter}
                onChange={(e) => setStartDryMatter(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
              <TextField
                label={t("rotationGrid.postCover") + " (kg/ha)"}
                type="number"
                size="small"
                value={endDryMatter}
                onChange={(e) => setEndDryMatter(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  mx: -2,
                  mb: -2,
                  mt: 0.5,
                  px: 2,
                  py: 1.5,
                  backgroundColor: "background.paper",
                  borderTop: "1px solid",
                  borderColor: "divider",
                  zIndex: 1,
                }}
              >
                <Button
                  size="small"
                  variant="contained"
                  disabled={dateOrderInvalid}
                  onClick={handleSubmit}
                  sx={{
                    flex: 1,
                    textTransform: "none",
                    backgroundColor: COLORS.dahlia[700],
                    "&:hover": { backgroundColor: COLORS.dahlia[900] },
                  }}
                >
                  {t("edit.button.save")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedField(null)}
                  sx={{ textTransform: "none" }}
                >
                  {t("edit.button.back")}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function RotationSeasonGrid({
  animalGroupId,
  animalGroup,
  demandKgDay,
}: {
  animalGroupId: string;
  animalGroup?: AnimalGroup;
  demandKgDay?: number;
}) {
  const { t } = useTranslation();
  const { data: moveEvents, isLoading: movesLoading } =
    useAnimalGroupMoveEvents(animalGroupId);
  const { data: allFields = [], isLoading: fieldsLoading } = useFields();
  const visibleMoveEvents = useMemo(
    () => visibleRotationMoveEvents(moveEvents ?? []),
    [moveEvents]
  );

  const assignedFields = useMemo(
    () => getAllFieldsForAnimalGroupIdFromArray(allFields, animalGroupId),
    [allFields, animalGroupId]
  );

  // Build field list: assigned fields + historical fields from moveEvents
  // (fields may no longer be assigned but had grazing events in past seasons)
  const unsortedFields = useMemo(() => {
    const fieldByGuid = new Map<string, Field>();
    for (const f of assignedFields) {
      fieldByGuid.set(f.guid, f);
    }
    for (const ev of visibleMoveEvents) {
      if (ev.fieldGuid && !fieldByGuid.has(ev.fieldGuid)) {
        const existing = allFields.find((f) => f.guid === ev.fieldGuid);
        if (existing) {
          fieldByGuid.set(ev.fieldGuid, existing);
        } else if (ev.fieldName) {
          fieldByGuid.set(ev.fieldGuid, {
            guid: ev.fieldGuid,
            name: ev.fieldName,
          } as Field);
        }
      }
    }
    return Array.from(fieldByGuid.values());
  }, [assignedFields, visibleMoveEvents, allFields]);

  const seasonYears = useMemo(
    () => getSeasonYears(visibleMoveEvents),
    [visibleMoveEvents]
  );
  const [selectedYear, setSelectedYear] = useState<number>(() =>
    new Date().getFullYear()
  );

  const currentYear = new Date().getFullYear();
  const hasDataForYear = seasonYears.includes(selectedYear);

  // Filter to fields relevant to this season: assigned fields + fields with events this year
  const seasonFields = useMemo(() => {
    const isCurrentSeason = selectedYear === new Date().getFullYear();
    const assignedGuids = new Set(assignedFields.map((f) => f.guid));
    const seasonEventGuids = new Set(
      visibleMoveEvents
        .filter((ev) => {
          if (!ev.fieldGuid) return false;
          // Mirror groupIntoRotations: open events only show in the current-
          // year view, never in the year they started in.
          if (ev.dateTimeOut === null) return isCurrentSeason;
          const seasonAnchor = ev.dateTimeIn ?? ev.dateTimeOut;
          if (!seasonAnchor) return false;
          return new Date(seasonAnchor).getFullYear() === selectedYear;
        })
        .map((ev) => ev.fieldGuid as string)
    );
    return unsortedFields.filter(
      (f) => assignedGuids.has(f.guid) || seasonEventGuids.has(f.guid)
    );
  }, [unsortedFields, assignedFields, visibleMoveEvents, selectedYear]);

  // Sort fields by rotation order for the selected season
  const fields = useMemo(
    () =>
      sortFieldsByRotationOrder(seasonFields, visibleMoveEvents, selectedYear),
    [seasonFields, visibleMoveEvents, selectedYear]
  );

  const overlapByGuid = useMemo(
    () => buildOverlapWarningMap(visibleMoveEvents),
    [visibleMoveEvents]
  );

  const { grid, rotationCount } = useMemo(
    () => buildGrid(fields, visibleMoveEvents, selectedYear, overlapByGuid),
    [fields, visibleMoveEvents, selectedYear, overlapByGuid]
  );

  // All grass fields on the farm, flagged by current assignment to this group.
  // Lets the farmer add events on fields previously grazed by this group but
  // not currently assigned to it.
  const candidateFields = useMemo(() => {
    const assignedSet = new Set(assignedFields.map((f) => f.guid));
    return allFields.map((f) => ({
      guid: f.guid,
      name: f.name,
      isAssigned: assignedSet.has(f.guid),
    }));
  }, [allFields, assignedFields]);

  // The field the animal group is currently grazing on, derived from the
  // youngest open move event so the "Move to shed" button reappears when a
  // new field-based open event exists. If the open event is a shed event,
  // animals are already in the shed — no button.
  const currentField = useMemo(() => {
    const openEvents = visibleMoveEvents.filter(
      (ev): ev is MoveEvent & { dateTimeIn: string } =>
        ev.dateTimeOut === null && ev.dateTimeIn != null
    );
    const youngest = openEvents.reduce<
      (MoveEvent & { dateTimeIn: string }) | undefined
    >((acc, ev) => {
      if (!acc) return ev;
      return new Date(ev.dateTimeIn).getTime() >
        new Date(acc.dateTimeIn).getTime()
        ? ev
        : acc;
    }, undefined);
    if (!youngest || youngest.movedToShed) return undefined;
    if (!youngest.fieldGuid) return undefined;
    return allFields.find((f) => f.guid === youngest.fieldGuid);
  }, [visibleMoveEvents, allFields]);
  const openMoveOutEventDialog = useUIStore(
    (state) => state.openMoveOutEventDialog
  );
  const handleMoveToShed = useMemo(
    () =>
      currentField
        ? () =>
            openMoveOutEventDialog({
              open: true,
              currentFieldWithAnimals: currentField,
              moveToShed: true,
              animalGroupGuid: animalGroupId,
            })
        : undefined,
    [currentField, openMoveOutEventDialog, animalGroupId]
  );

  const rotationColumns = useMemo(
    () => buildRotationColumns(visibleMoveEvents, selectedYear, overlapByGuid),
    [visibleMoveEvents, selectedYear, overlapByGuid]
  );

  // Season date range
  const {
    seasonStart,
    seasonEnd,
    isCurrentSeason,
    warnings: seasonWarnings,
  } = useMemo(
    () => getSeasonDateRange(visibleMoveEvents, selectedYear, t),
    [visibleMoveEvents, selectedYear, t]
  );

  // Rotation totals (days per rotation column)
  const rotationTotals = useMemo(() => {
    const totals: number[] = [];
    for (let rot = 0; rot < rotationCount; rot++) {
      let total = 0;
      for (const row of grid) {
        const cell = row[rot];
        if (cell?.dateIn && cell?.dateOut) {
          total += Math.max(
            0,
            (new Date(cell.dateOut).getTime() -
              new Date(cell.dateIn).getTime()) /
              86400000
          );
        }
      }
      totals.push(Math.round(total * 10) / 10);
    }
    return totals;
  }, [grid, rotationCount]);

  // Rotation analysis — demand comes from the active feed budget.
  const dailyDemand = demandKgDay ?? 0;
  const postGrazingTarget = animalGroup?.postGrazingTarget ?? 0;

  const analysis: RotationAnalysis | null = useMemo(() => {
    if (!fields.length || !visibleMoveEvents.length) return null;
    return analyzeRotation(
      fields,
      visibleMoveEvents,
      postGrazingTarget,
      dailyDemand,
      selectedYear
    );
  }, [fields, visibleMoveEvents, postGrazingTarget, dailyDemand, selectedYear]);

  // Build lookup maps for UI
  const outlierKeys = useMemo(() => {
    const set = new Set<string>();
    if (!analysis) return set;
    for (const ev of analysis.likelyPath.outliers) {
      if (ev.fieldGuid) set.add(`${ev.fieldGuid}:${ev.dateTimeIn}`);
    }
    return set;
  }, [analysis]);

  const estimateByField = useMemo(() => {
    const map: Record<string, number> = {};
    if (!analysis) return map;
    for (const est of analysis.fieldEstimates) {
      map[est.fieldGuid] = est.estimatedDays;
    }
    return map;
  }, [analysis]);

  const queryClient = useQueryClient();
  const updateReading = useUpdateReading();
  const createReading = useCreateReading();
  const deleteReading = useDeleteReading();

  // Briefly highlight whichever event the user just touched. Helps the
  // farmer follow an edited event to its new position when sorting changes.
  const [lastEditedGuid, setLastEditedGuid] = useState<string | null>(null);
  const flagEdited = useCallback((guid: string) => {
    setLastEditedGuid(guid);
    setTimeout(
      () => setLastEditedGuid((current) => (current === guid ? null : current)),
      2200
    );
  }, []);

  const invalidateMoveEvents = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.animalGroups.moveEvents(animalGroupId),
    });
  }, [queryClient, animalGroupId]);

  const handleAccept = useCallback(
    (cell: GridCell) => {
      if (!cell.moveEvent) return;
      const ev = cell.moveEvent;
      const isMachineProposed =
        ev.kind === "machine" && ev.verificationStatus === "Proposed";

      if (isMachineProposed) {
        // Reject the proposed machine record (tombstone — stops the pipeline
        // from re-proposing the slot) and replace it with an identical
        // machine record marked Accepted. The tombstone passes the existing
        // animal group (which may be null) so the rejection does not attach
        // an otherwise-unattached row as a side effect.
        updateReading.mutate(
          buildReadingPayload(ev, cell.fieldGuid, ev.animalGroupGuid, {
            verificationStatus: "Rejected",
          }),
          {
            onSuccess: () => {
              createReading.mutate(
                {
                  ...buildReadingPayload(ev, cell.fieldGuid, animalGroupId, {
                    verificationStatus: "Accepted",
                  }),
                  guid: guid(),
                },
                {
                  onSuccess: (data) => {
                    flagEdited(data.guid);
                    invalidateMoveEvents();
                  },
                }
              );
            },
          }
        );
      } else {
        updateReading.mutate(
          buildReadingPayload(ev, cell.fieldGuid, animalGroupId, {
            verificationStatus: "Accepted",
          }),
          {
            onSuccess: () => {
              flagEdited(ev.guid);
              invalidateMoveEvents();
            },
          }
        );
      }
    },
    [
      updateReading,
      createReading,
      invalidateMoveEvents,
      animalGroupId,
      flagEdited,
    ]
  );

  const handleSaveEditInline = useCallback(
    (
      cell: GridCell,
      values: {
        // Full ISO timestamps (the cell handles same-day preservation and
        // optional time-editing before sending).
        dateIn: string;
        dateOut: string;
        startDryMatter: number | null;
        endDryMatter: number | null;
      }
    ) => {
      if (!values.dateIn) return;
      const newDateIn = values.dateIn;
      const newDateOut = values.dateOut || null;
      const startDryMatter = newDateIn ? values.startDryMatter : null;
      const endDryMatter = newDateOut ? values.endDryMatter : null;

      if (cell.moveEvent) {
        const ev = cell.moveEvent;
        const isMachineProposed =
          ev.kind === "machine" && ev.verificationStatus === "Proposed";

        if (isMachineProposed) {
          // Editing a proposed machine reading: reject the original
          // (tombstone — stops the pipeline from re-proposing the slot) and
          // write a fresh machine record marked Accepted with the edited
          // values. The tombstone passes the existing animal group (which
          // may be null) so the rejection does not attach an
          // otherwise-unattached row as a side effect.
          // Already-accepted machine readings and user readings are updated
          // in place.
          updateReading.mutate(
            buildReadingPayload(ev, cell.fieldGuid, ev.animalGroupGuid, {
              verificationStatus: "Rejected",
            }),
            {
              onSuccess: () => {
                createReading.mutate(
                  {
                    guid: guid(),
                    manualDryMatterInput: {
                      field: cell.fieldGuid,
                      dateTimeMeasurementStart: newDateIn,
                      dateTimeMeasurementEnd: newDateOut ?? newDateIn,
                      event: {
                        eventType: EventType.GrazingEvent,
                        dateTimeIn: newDateIn,
                        dateTimeOut: newDateOut ?? undefined,
                        preEventCover: startDryMatter ?? undefined,
                        postEventCover: endDryMatter ?? undefined,
                        animalGroupGuid: animalGroupId,
                      },
                      kind: "machine",
                      verificationStatus: "Accepted",
                    },
                  },
                  {
                    onSuccess: (data) => {
                      flagEdited(data.guid);
                      invalidateMoveEvents();
                    },
                  }
                );
              },
            }
          );
        } else {
          updateReading.mutate(
            buildReadingPayload(ev, cell.fieldGuid, animalGroupId, {
              dateTimeIn: newDateIn,
              dateTimeOut: newDateOut,
              startDryMatter,
              endDryMatter,
            }),
            {
              onSuccess: () => {
                flagEdited(ev.guid);
                invalidateMoveEvents();
              },
            }
          );
        }
      } else {
        createReading.mutate(
          {
            guid: guid(),
            manualDryMatterInput: {
              field: cell.fieldGuid,
              dateTimeMeasurementStart: newDateIn,
              dateTimeMeasurementEnd: newDateOut ?? newDateIn,
              event: {
                eventType: EventType.GrazingEvent,
                dateTimeIn: newDateIn,
                dateTimeOut: newDateOut ?? undefined,
                preEventCover: startDryMatter ?? undefined,
                postEventCover: endDryMatter ?? undefined,
                animalGroupGuid: animalGroupId,
              },
              kind: "user",
              verificationStatus: "Accepted",
            },
          },
          {
            onSuccess: (data) => {
              flagEdited(data.guid);
              invalidateMoveEvents();
            },
          }
        );
      }
    },
    [
      updateReading,
      createReading,
      invalidateMoveEvents,
      animalGroupId,
      flagEdited,
    ]
  );

  const handleFixEndDate = useCallback(
    (cell: GridCell, suggestedEnd: Date) => {
      if (cell.moveEvent) {
        updateReading.mutate(
          buildReadingPayload(cell.moveEvent, cell.fieldGuid, animalGroupId, {
            dateTimeOut: suggestedEnd.toISOString(),
          }),
          { onSuccess: () => invalidateMoveEvents() }
        );
      } else if (cell.dateIn) {
        const newReading: ManualDMInputResponse = {
          guid: guid(),
          manualDryMatterInput: {
            field: cell.fieldGuid,
            dateTimeMeasurementStart: cell.dateIn,
            dateTimeMeasurementEnd: suggestedEnd.toISOString(),
            event: {
              eventType: EventType.GrazingEvent,
              dateTimeIn: cell.dateIn,
              dateTimeOut: suggestedEnd.toISOString(),
              animalGroupGuid: animalGroupId,
            },
            kind: "user",
            verificationStatus: "Accepted",
          },
        };
        createReading.mutate(newReading, {
          onSuccess: () => invalidateMoveEvents(),
        });
      }
    },
    [updateReading, createReading, invalidateMoveEvents, animalGroupId]
  );

  const handleDelete = useCallback(
    (cell: GridCell) => {
      if (!cell.moveEvent) return;
      const ev = cell.moveEvent;
      // Tombstones (soft delete via Rejected) only apply to machine-Proposed
      // readings — they keep the pipeline from re-proposing the slot. All
      // other readings are hard-deleted.
      const isMachineProposed =
        ev.kind === "machine" && ev.verificationStatus === "Proposed";

      if (isMachineProposed) {
        // The tombstone passes the existing animal group (which may be
        // null) so the rejection does not attach an otherwise-unattached
        // row as a side effect.
        updateReading.mutate(
          buildReadingPayload(ev, cell.fieldGuid, ev.animalGroupGuid, {
            verificationStatus: "Rejected",
          }),
          { onSuccess: () => invalidateMoveEvents() }
        );
      } else {
        deleteReading.mutate(
          {
            readingId: ev.guid,
            fieldId: cell.fieldGuid,
            isGrazing: true,
            animalGroupGuid: ev.animalGroupGuid ?? undefined,
          },
          { onSuccess: () => invalidateMoveEvents() }
        );
      }
    },
    [updateReading, deleteReading, invalidateMoveEvents, animalGroupId]
  );

  const handleAddEvent = useCallback(
    (
      cell: GridCell,
      suggestion: { suggestedIn: Date | null; suggestedOut: Date | null },
      biomass: { startDryMatter: number | null; endDryMatter: number | null }
    ) => {
      const dateIn = suggestion.suggestedIn ?? new Date();
      const dateOut = suggestion.suggestedOut ?? undefined;

      const newReading: ManualDMInputResponse = {
        guid: guid(),
        manualDryMatterInput: {
          field: cell.fieldGuid,
          dateTimeMeasurementStart: dateIn.toISOString(),
          dateTimeMeasurementEnd: (dateOut ?? dateIn).toISOString(),
          event: {
            eventType: EventType.GrazingEvent,
            dateTimeIn: dateIn.toISOString(),
            dateTimeOut: dateOut?.toISOString(),
            preEventCover: suggestion.suggestedIn
              ? biomass.startDryMatter ?? undefined
              : undefined,
            postEventCover: suggestion.suggestedOut
              ? biomass.endDryMatter ?? undefined
              : undefined,
            animalGroupGuid: animalGroupId,
          },
          kind: "user",
          verificationStatus: "Accepted",
        },
      };

      createReading.mutate(newReading, {
        onSuccess: (data) => {
          flagEdited(data.guid);
          invalidateMoveEvents();
        },
      });
    },
    [createReading, invalidateMoveEvents, animalGroupId, flagEdited]
  );

  if (movesLoading || fieldsLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!visibleMoveEvents.length) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("rotationTimeline.empty")}
        </Typography>
      </Box>
    );
  }

  const seasonDateFmt: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
  };

  return (
    <Box>
      <Box
        sx={{
          ...SECTION_CARD_SX,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Year navigator — centered */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            size="small"
            onClick={() => setSelectedYear((y) => y - 1)}
            sx={{ minWidth: 0, p: 0.5, color: COLORS.bellflower[500] }}
          >
            <ChevronLeftIcon sx={{ fontSize: 24 }} />
          </Button>
          <Typography
            sx={{
              fontWeight: 800,
              color: COLORS.dahlia[900],
              fontSize: "1.4rem",
              minWidth: 60,
              textAlign: "center",
              letterSpacing: "0.02em",
            }}
          >
            {selectedYear}
          </Typography>
          <Button
            size="small"
            onClick={() => setSelectedYear((y) => y + 1)}
            disabled={selectedYear >= currentYear}
            sx={{ minWidth: 0, p: 0.5, color: COLORS.bellflower[500] }}
          >
            <ChevronRightIcon sx={{ fontSize: 24 }} />
          </Button>
        </Box>

        {/* Season date range — below year */}
        {seasonStart && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", mt: 0.25 }}
          >
            {seasonStart.toLocaleDateString(undefined, seasonDateFmt)}
            {" — "}
            {seasonEnd
              ? seasonEnd.toLocaleDateString(undefined, seasonDateFmt)
              : t("rotationGrid.ongoing")}
            {seasonEnd &&
              ` · ${getDaysBetween(
                seasonStart.toISOString(),
                seasonEnd.toISOString()
              )} ${t("rotationGrid.days")}`}
          </Typography>
        )}
      </Box>

      {/* Empty state for years with no data */}
      {rotationColumns.length === 0 && (
        <Box
          sx={{
            py: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            color: "text.secondary",
          }}
        >
          <Typography
            variant="body1"
            sx={{ fontWeight: 500, mb: 0.5, color: COLORS.bellflower[700] }}
          >
            {t("rotationGrid.emptyYear", { year: selectedYear })}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.disabled",
              maxWidth: 320,
              textAlign: "center",
              mb: 2,
            }}
          >
            {t("rotationGrid.emptyYearHint")}
          </Typography>
          <AddMissingEventPicker
            fields={candidateFields}
            lastEventEnd={null}
            animalGroupName={animalGroup?.name}
            onAdd={(fieldGuid, dateIn, dateOut, biomass) => {
              const newReading: ManualDMInputResponse = {
                guid: guid(),
                manualDryMatterInput: {
                  field: fieldGuid,
                  dateTimeMeasurementStart: dateIn,
                  dateTimeMeasurementEnd: dateOut ?? dateIn,
                  event: {
                    eventType: EventType.GrazingEvent,
                    dateTimeIn: dateIn,
                    dateTimeOut: dateOut ?? undefined,
                    preEventCover: biomass.startDryMatter ?? undefined,
                    postEventCover: biomass.endDryMatter ?? undefined,
                    animalGroupGuid: animalGroupId,
                  },
                  kind: "user",
                  verificationStatus: "Accepted",
                },
              };
              createReading.mutate(newReading, {
                onSuccess: () => invalidateMoveEvents(),
              });
            }}
            onMoveToShed={isCurrentSeason ? handleMoveToShed : undefined}
          />
        </Box>
      )}

      {/* Rotation columns */}
      {rotationColumns.length > 0 && (
        <Box
          sx={{
            ...SECTION_CARD_SX,
            p: { xs: 1, sm: 1.5 },
            mb: 0,
          }}
        >
          <Box sx={{ overflowX: "auto", pb: 1, mx: -1, px: 1 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${rotationColumns.length}, minmax(160px, 260px))`,
                gap: 2,
                alignItems: "start",
                width: "fit-content",
                mx: "auto",
              }}
            >
              {rotationColumns.map((col, colIndex) => (
                <Box key={col.rotation}>
                  {/* Column header */}
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 0.75,
                      mb: 0.5,
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      backgroundColor: "white",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: COLORS.bellflower[700],
                        fontSize: "0.7rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        lineHeight: 1.2,
                      }}
                    >
                      {t("rotationGrid.rotation", { number: col.rotation })}
                    </Typography>
                    {col.totalDays > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.disabled",
                          fontSize: "0.6rem",
                          display: "block",
                          lineHeight: 1.2,
                        }}
                      >
                        {col.totalDays}d total
                      </Typography>
                    )}
                  </Box>

                  {/* Stacked event cards */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    {col.events.map((cell, ci) => (
                      <GrazingEventCell
                        key={`${cell.fieldGuid}-${cell.dateIn}-${ci}`}
                        cell={cell}
                        onSaveEdit={handleSaveEditInline}
                        onAddEvent={handleAddEvent}
                        onDelete={handleDelete}
                        isAddingEvent={createReading.isPending}
                        isJustEdited={cell.moveEvent?.guid === lastEditedGuid}
                        grid={grid}
                        fieldIndex={0}
                        rotationIndex={ci}
                      />
                    ))}

                    {/* Add event picker */}
                    {candidateFields.length > 0 && (
                      <AddMissingEventPicker
                        fields={candidateFields}
                        lastEventEnd={
                          col.events[col.events.length - 1]?.dateOut ??
                          col.events[col.events.length - 1]?.dateIn ??
                          null
                        }
                        animalGroupName={animalGroup?.name}
                        onAdd={(fieldGuid, dateIn, dateOut, biomass) => {
                          const newReading: ManualDMInputResponse = {
                            guid: guid(),
                            manualDryMatterInput: {
                              field: fieldGuid,
                              dateTimeMeasurementStart: dateIn,
                              dateTimeMeasurementEnd: dateOut ?? dateIn,
                              event: {
                                eventType: EventType.GrazingEvent,
                                dateTimeIn: dateIn,
                                dateTimeOut: dateOut ?? undefined,
                                preEventCover:
                                  biomass.startDryMatter ?? undefined,
                                postEventCover:
                                  biomass.endDryMatter ?? undefined,
                                animalGroupGuid: animalGroupId,
                              },
                              kind: "user",
                              verificationStatus: "Accepted",
                            },
                          };
                          createReading.mutate(newReading, {
                            onSuccess: () => invalidateMoveEvents(),
                          });
                        }}
                        onMoveToShed={
                          isCurrentSeason &&
                          colIndex === rotationColumns.length - 1
                            ? handleMoveToShed
                            : undefined
                        }
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
